import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Add or correct one crane, native to the console.
 *
 * **The form is generated from the server's import schema, not typed out here.** `CraneImportSchema` already
 * drives the XLSX template — its own comment says the template is generated "so what a person downloads cannot
 * drift from what the parser accepts" — and hand-copying forty-odd fields across four namespaces into React
 * would reintroduce precisely that drift. The first schema change would leave this form offering a field the
 * importer rejects, or omitting one it requires, and nothing would fail loudly. `GET /admin/imports/assets/schema`
 * serves the same definition both surfaces read.
 *
 * That also settles which fields are mandatory: `required` here is what `AssetImportService` enforces, rather
 * than a second opinion about it that can disagree.
 *
 * Load-test columns are deliberately **not** offered. They belong to `compliance_item`, not to the asset —
 * CLAUDE.md rule 13, derive don't duplicate — and are recorded through the compliance path so the crane detail
 * and the compliance report cannot disagree about a statutory examination.
 */
export default function CraneEntry() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const editCode = params.get('code') || ''

  const schema = useApi('/admin/imports/assets/schema')
  const assets = useApi('/assets')
  const [values, setValues] = useState({})
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [editing, setEditing] = useState(null)   // the asset being corrected, or null for a new crane

  const all = useMemo(() => (Array.isArray(assets.data) ? assets.data : []), [assets.data])

  // Deep-linked from the crane register's Edit action.
  useEffect(() => {
    if (!editCode || !all.length) return
    const hit = all.find((a) => a.assetCode === editCode)
    if (hit) load(hit)
  }, [editCode, all])

  /** Flatten an asset back into the schema's flat field names, so the same form edits it. */
  function load(asset) {
    setEditing(asset)
    setNotice(null)
    const attrs = parseAttrs(asset.attributes)
    const next = {
      crane_no: asset.assetCode,
      name: asset.name,
      department: asset.department,
      area: asset.area,
      bay: asset.bay || '',
      location_desc: asset.locationDesc || '',
      equipment_type_code: asset.equipmentTypeCode || 'CRANE',
      install_date: asset.installDate || '',
      warranty_until: asset.warrantyUntil || '',
    }
    // Attribute-backed fields come from the namespaced JSON by their schema target path.
    cols(schema.data).forEach((c) => {
      if (c.dest === 'ATTRIBUTE') next[c.name] = attrs[c.target] ?? ''
    })
    setValues(next)
  }

  function reset() {
    setEditing(null)
    setValues({})
    setNotice(null)
  }

  async function submit(e) {
    e.preventDefault()
    setBusy(true)
    setNotice(null)
    try {
      const body = toRequest(values, cols(schema.data))
      if (editing) {
        // PUT, and assetCode is absent from the update DTO: the crane number is identity. The importer says
        // the same thing — "Never changes on update" — so the console must not offer to change it either.
        await api(`/assets/${editing.id}`, { method: 'PUT', body })
        setNotice({ kind: 'success', text: `${values.crane_no} updated.` })
      } else {
        await api('/assets', { method: 'POST', body })
        setNotice({ kind: 'success', text: `${values.crane_no} added to the fleet.` })
        setValues({})
      }
      assets.reload()
    } catch (err) {
      // The server's own message: a duplicate crane number, an unknown equipment type, a bad attribute
      // against the type's JSON schema. All of those are actionable and none of them are "save failed".
      setNotice({ kind: 'danger', text: err.message })
    } finally {
      setBusy(false)
    }
  }

  const groups = useMemo(() => groupBySheet(schema.data), [schema.data])
  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }))
  const missing = requiredMissing(values, cols(schema.data))

  return (
    <>
      <PageHeader
        title={editing ? `Correct ${editing.assetCode}` : 'Single crane entry'}
        subtitle="For a whole fleet use the Excel import — this is for one crane, or a correction."
        actions={
          <>
            {editing && <Button variant="secondary" onClick={reset}>New crane instead</Button>}
            <Button variant="secondary" onClick={() => nav('/admin/import')}>Bulk import</Button>
          </>
        }
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}
      {schema.loading && <Loading rows={6} />}
      {schema.error && <ErrorState error={schema.error} onRetry={schema.reload} />}

      {schema.data && (
        <form onSubmit={submit}>
          {groups.map(([sheet, list]) => (
            <Card key={sheet} title={sheetLabel(sheet)} subtitle={sheetHint(sheet)}>
              <div className="grid-2">
                {list
                  // The key column is rendered in the first group only, and load-test fields never (rule 13).
                  .filter((c) => c.dest !== 'LOAD_TEST')
                  .filter((c) => sheet === 'BaseData' || c.name !== 'crane_no')
                  .map((c) => (
                    <Field
                      key={c.name}
                      id={`f-${c.name}`}
                      label={fieldLabel(c) + (c.required ? ' *' : '')}
                      hint={c.note || undefined}
                    >
                      {c.enumValues?.length ? (
                        <select id={`f-${c.name}`} value={values[c.name] ?? ''} onChange={set(c.name)}>
                          <option value="">—</option>
                          {c.enumValues.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : (
                        <input
                          id={`f-${c.name}`}
                          // The schema's Kind drives the input type, so a number field rejects text on the
                          // client for the same reason the parser does on the server.
                          type={inputType(c.kind)}
                          step={c.kind === 'NUMBER' ? 'any' : undefined}
                          value={values[c.name] ?? ''}
                          onChange={set(c.name)}
                          disabled={Boolean(editing) && c.name === 'crane_no'}
                          required={c.required}
                        />
                      )}
                    </Field>
                  ))}
              </div>
            </Card>
          ))}

          <Banner kind="info">
            Load-test dates are not on this form. A statutory examination is a compliance record, not a crane
            attribute — recorded once so the crane detail and the compliance report cannot disagree.
          </Banner>

          <div className="row" style={{ marginTop: 'var(--md)' }}>
            <Button type="submit" disabled={busy || missing.length > 0}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add crane'}
            </Button>
            {missing.length > 0 && (
              <span className="muted">
                Required: {missing.map((c) => fieldLabel(c)).join(', ')}
              </span>
            )}
          </div>
        </form>
      )}

      <SectionHeader hint="Click a crane to correct it">Fleet</SectionHeader>
      <Card>
        {assets.loading && <Loading rows={5} />}
        {assets.data && (
          <Table
            keyOf={(a) => a.id}
            onRowClick={(a) => load(a)}
            columns={[
              { key: 'assetCode', header: 'Crane no', render: (a) => <strong>{a.assetCode}</strong> },
              { key: 'name', header: 'Name' },
              { key: 'department', header: 'Department' },
              { key: 'area', header: 'Area', render: (a) => [a.area, a.bay].filter(Boolean).join(' · ') },
              {
                key: 'status',
                header: 'Status',
                // Same single-pill rule as the crane register: do_not_operate is the operational truth, so a
                // crane never reads ACTIVE and LOCKED OUT at once (reported on CRANE-04).
                render: (a) => (a.doNotOperate
                  ? <StatusPill status="LOCKED_OUT" title={a.doNotOperateReason || 'Do not operate'} />
                  : <StatusPill status={a.status} />),
              },
            ]}
            rows={all}
            empty={<p className="muted">No cranes yet. Add one above, or import the fleet.</p>}
          />
        )}
      </Card>
    </>
  )
}

/* ---- Schema helpers ------------------------------------------------------ */

const cols = (schema) => (schema ? Object.values(schema).flat() : [])

/** Sheets in the server's own order, so the form reads like the template. */
function groupBySheet(schema) {
  if (!schema) return []
  return Object.entries(schema)
}

const SHEET_LABELS = {
  BaseData: 'Identity and rating',
  Electrical: 'Electrical specification',
  Mechanical: 'Mechanical specification',
  Safety: 'Safety devices',
}
const SHEET_HINTS = {
  BaseData: 'Crane number, location, capacity and hoists. Crane number and the starred fields are required.',
  Electrical: 'Supply, control gear and drives.',
  Mechanical: 'Travel, wheels and structure.',
  Safety: 'Limit switches, overload protection and interlocks.',
}
const sheetLabel = (s) => SHEET_LABELS[s] || s
const sheetHint = (s) => SHEET_HINTS[s] || undefined

/** `mh_capacity_t` -> "Mh capacity t" is poor; expand the abbreviations a plant actually uses. */
function fieldLabel(c) {
  const raw = c.name
    .replace(/^mh_/, 'Main hoist ')
    .replace(/^ah_/, 'Aux hoist ')
    .replace(/_t$/, ' (t)')
    .replace(/_m$/, ' (m)')
    .replace(/_mm$/, ' (mm)')
    .replace(/_kw$/, ' (kW)')
    .replace(/_v$/, ' (V)')
    .replace(/_hz$/, ' (Hz)')
    .replace(/_m_min$/, ' (m/min)')
    .replace(/_pct$/, ' (%)')
    .replace(/_no$/, ' no')
    .replace(/_/g, ' ')
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

const inputType = (kind) => (kind === 'DATE' ? 'date' : kind === 'NUMBER' || kind === 'INTEGER' ? 'number' : 'text')

function requiredMissing(values, list) {
  return list.filter((c) => c.required && c.dest !== 'LOAD_TEST' && !String(values[c.name] ?? '').trim())
}

/**
 * Flat form values -> the `AssetRequest` shape, rebuilding the namespaced attribute tree.
 *
 * Attributes are nested by their dotted target path (`hoists.main.capacity_t`) rather than flattened, because
 * `AttributeSchemaValidator` validates the namespaced document against the EquipmentType's JSON schema. Sending
 * a flat map would fail validation, and sending the wrong namespace would put a crane's rope diameter somewhere
 * no screen reads — the shape of defect CLAUDE.md rule 11 exists to prevent.
 */
function toRequest(values, list) {
  const attributes = {}
  const body = {}

  list.forEach((c) => {
    if (c.dest === 'LOAD_TEST') return
    const raw = values[c.name]
    if (raw === undefined || String(raw).trim() === '') return
    const v = c.kind === 'NUMBER' || c.kind === 'INTEGER' ? Number(raw) : String(raw).trim()

    if (c.dest === 'COLUMN') {
      body[c.target] = v
    } else {
      // Walk the dotted path, creating namespaces as needed.
      const parts = c.target.split('.')
      let node = attributes
      parts.slice(0, -1).forEach((p) => { node[p] = node[p] || {}; node = node[p] })
      node[parts[parts.length - 1]] = v
    }
  })

  return {
    equipmentTypeCode: body.equipmentTypeCode || 'CRANE',
    assetCode: body.assetCode,
    name: body.name,
    department: body.department,
    area: body.area,
    bay: body.bay || null,
    locationDesc: body.locationDesc || null,
    installDate: body.installDate || null,
    warrantyUntil: body.warrantyUntil || null,
    // The server stores this column as JSONB text.
    attributes: JSON.stringify(attributes),
  }
}

/** Namespaced attribute JSON -> dotted keys, for editing an existing crane. */
function parseAttrs(json) {
  if (!json) return {}
  const out = {}
  const walk = (obj, prefix) => {
    Object.entries(obj || {}).forEach(([k, v]) => {
      const path = prefix ? `${prefix}.${k}` : k
      if (v && typeof v === 'object' && !Array.isArray(v)) walk(v, path)
      else out[path] = v
    })
  }
  try { walk(JSON.parse(json), '') } catch { /* malformed attributes render as empty */ }
  return out
}
